/**
 * Store quiz completion in a cookie so guests can't redo the same topic on the same day.
 * Cookie format: cd_quiz_completed = JSON of { "YYYY-MM-DD": { "1": 8, "2": 10, ... } } (topic -> questionsCorrect)
 * or legacy: { "YYYY-MM-DD": [1,2,3] } (topic slots only)
 * Max-Age: 7 days
 */
import { getCurrentQuizDate } from './quizTime';

const COOKIE_NAME = 'cd_quiz_completed';
const MAX_AGE_DAYS = 7;

type CompletionMap = Record<string, number[] | Record<number, number>>;

function parseCookie(): CompletionMap {
  if (typeof document === 'undefined') return {};
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
    if (!match) return {};
    const decoded = decodeURIComponent(match[1]);
    const parsed = JSON.parse(decoded) as CompletionMap;
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function writeCookie(data: CompletionMap): void {
  if (typeof document === 'undefined') return;
  const json = JSON.stringify(data);
  const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(json)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

export function isTopicCompletedForDate(topicSlot: number, date?: string): boolean {
  const d = date ?? getCurrentQuizDate();
  const map = parseCookie();
  const slots = map[d];
  if (Array.isArray(slots)) return slots.includes(topicSlot);
  if (slots && typeof slots === 'object' && !Array.isArray(slots)) {
    return Object.prototype.hasOwnProperty.call(slots, topicSlot);
  }
  return false;
}

export function markTopicCompleted(topicSlot: number, questionsCorrect: number, date?: string): void {
  const d = date ?? getCurrentQuizDate();
  const map = parseCookie();
  let dayData = map[d];
  if (!dayData) {
    dayData = {};
    map[d] = dayData;
  }
  // Migrate legacy array format to object
  if (Array.isArray(dayData)) {
    const obj: Record<number, number> = {};
    for (const s of dayData) obj[s] = 0;
    dayData = obj;
    map[d] = dayData;
  }
  (dayData as Record<number, number>)[topicSlot] = questionsCorrect;
  writeCookie(map);
}

export function getCompletedTopicsForDate(date?: string): number[] {
  const d = date ?? getCurrentQuizDate();
  const map = parseCookie();
  const slots = map[d];
  if (Array.isArray(slots)) return slots;
  if (slots && typeof slots === 'object' && !Array.isArray(slots)) {
    return Object.keys(slots).map(Number);
  }
  return [];
}

export function getGuestScoreForTopic(topicSlot: number, date?: string): number | undefined {
  const d = date ?? getCurrentQuizDate();
  const map = parseCookie();
  const dayData = map[d];
  if (dayData && typeof dayData === 'object' && !Array.isArray(dayData)) {
    const val = (dayData as Record<number, number>)[topicSlot];
    return val !== undefined ? val : undefined;
  }
  return undefined;
}
